"use client";

import React from "react";
import { Pagination as AntPagination, ConfigProvider, Select } from "antd";
import type { PaginationProps as AntPaginationProps } from "antd";
import { orionTheme } from "../theme/orion-theme";
import clsx from "clsx";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import "../styles/pagination.css";

export interface PaginationProps extends Omit<AntPaginationProps, "showTotal"> {
  /**
   * Additional CSS classes (supports Tailwind overrides)
   */
  className?: string;

  /**
   * Show total items count (boolean or function)
   */
  showTotal?: boolean | ((total: number, range: [number, number]) => string);

  /**
   * Show items per page selector
   */
  showSizeChanger?: boolean;

  /**
   * Items per page options
   */
  pageSizeOptions?: string[];

  /**
   * Custom total text format
   */
  totalText?: string;
}

/**
 * Pagination component - Ant Design Pagination with OpenStore Orion theming
 *
 * This component provides FULL access to all Ant Design Pagination props while
 * automatically applying the OpenStore Orion theme and Figma design specifications.
 *
 * Features:
 * - Figma design specifications (colors, spacing, typography)
 * - Automatic theming
 * - Tailwind class override support
 * - ALL Ant Design Pagination features and props
 * - Custom total items display
 * - Items per page selector
 *
 * @example
 * ```tsx
 * // Basic pagination with Figma styling
 * <Pagination
 *   current={1}
 *   total={300}
 *   pageSize={10}
 *   showTotal
 *   showSizeChanger
 * />
 *
 * // All Ant Design Pagination props work
 * <Pagination
 *   current={currentPage}
 *   total={totalItems}
 *   pageSize={pageSize}
 *   onChange={handlePageChange}
 *   onShowSizeChange={handlePageSizeChange}
 *   showQuickJumper
 *   showSizeChanger
 *   pageSizeOptions={['10', '20', '50', '100']}
 *   showTotal={(total, range) => `Showing ${range[0]}-${range[1]} of ${total} items`}
 *   disabled={isLoading}
 *   simple
 *   size="small"
 *   className="custom-pagination"
 * />
 *
 * // With custom total text
 * <Pagination
 *   current={1}
 *   total={300}
 *   totalText="Total 300 Items"
 *   showSizeChanger
 * />
 * ```
 */
export const Pagination: React.FC<PaginationProps> = ({
  className,
  showTotal = true,
  showSizeChanger = true,
  pageSizeOptions = ["5", "10", "20", "50", "100"],
  totalText,
  ...props
}) => {
  const defaultTotalText = (total: number) => `Total ${total} Items`;

  return (
    <ConfigProvider theme={orionTheme}>
      <div className={clsx("orion-pagination-container", className)}>
        <div className="orion-pagination-content">
          {/* Total items text */}
          {showTotal && (
            <div className="orion-pagination-total">
              {typeof showTotal === "function"
                ? showTotal(props.total || 0, [1, props.pageSize || 10])
                : totalText || defaultTotalText(props.total || 0)}
            </div>
          )}

          {/* Pagination controls */}
          <AntPagination
            className="orion-pagination"
            itemRender={(page, type, originalElement) => {
              if (type === "prev") {
                return <LeftOutlined className="orion-pagination-icon" />;
              }
              if (type === "next") {
                return <RightOutlined className="orion-pagination-icon" />;
              }
              return originalElement;
            }}
            showTotal={typeof showTotal === "function" ? showTotal : undefined}
            {...props}
          />

          {/* Items per page selector */}
          {showSizeChanger && (
            <div className="orion-pagination-size-changer">
              <span className="orion-pagination-size-text">Show per page</span>
              <Select
                value={props.pageSize?.toString() || "10"}
                onChange={(value) => props.onShowSizeChange?.(1, Number(value))}
                options={pageSizeOptions.map((size) => ({
                  value: size,
                  label: size,
                }))}
                className="orion-pagination-select"
                size="small"
              />
            </div>
          )}
        </div>
      </div>
    </ConfigProvider>
  );
};

Pagination.displayName = "Pagination";

// Export convenience components for common patterns
export const SimplePagination: React.FC<Omit<PaginationProps, "simple">> = (
  props
) => <Pagination simple {...props} />;

export const CompactPagination: React.FC<Omit<PaginationProps, "size">> = (
  props
) => <Pagination size="small" {...props} />;

export const MinimalPagination: React.FC<
  Omit<PaginationProps, "showTotal" | "showSizeChanger">
> = (props) => (
  <Pagination showTotal={false} showSizeChanger={false} {...props} />
);
